import React, { useState, useEffect } from "react";
import { customerStatusLabel, isVipCustomer } from "../../utils/customerStatus";
import { useNavigate, useSearchParams } from "react-router-dom";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { FaFilter, FaEye } from "react-icons/fa";
import StaffLayout from "./StaffLayout";
import EsignStatusIcon from "./EsignStatusIcon";
import CustomerNameLink from "./CustomerNameLink";
import "./CustomerList.css";

/**
 * Component to display product indicators as stylish badges
 */
const ProductIndicators = ({ customer }) => {
  const getUnifiedMembershipDisplayName = () => {
    const name = String(customer.sharedMembershipName || "").trim();
    if (name && name.toLowerCase() !== "none" && name !== "Shared Membership") {
      return name;
    }

    const monthlyCredits =
      customer.sharedMembershipSessionUsage?.totalSessions ||
      customer.sharedMembershipSessionUsage?.remaining ||
      0;

    if (monthlyCredits === 4) {
      return "Diviti Glow";
    }

    if (monthlyCredits === 8) {
      return "Diviti Radiance";
    }

    if (monthlyCredits >= 12) {
      return "Diviti Elite";
    }

    return "Flexible Session Credits Membership";
  };

  const hasAvailableOrBookedCredits = (usage) =>
    Boolean(usage && ((usage.remaining || 0) > 0 || (usage.booked || 0) > 0));

  const hasUnexpiredAvailableCredits = (usage, expiresAt) =>
    hasAvailableOrBookedCredits(usage) &&
    (!expiresAt || new Date(expiresAt).getTime() > Date.now());

  // Helper function to determine product indicators
  const getProductIndicators = () => {
    const indicators = [];

    // Corporate badges come first because they affect booking/payment rules.
    if (customer.corporateProgramStatus?.enrolled) {
      indicators.push({
        code: "CP",
        label: `Corporate Program: ${
          customer.corporateProgramStatus.organizationCode || "Active"
        }${
          customer.corporateProgramStatus.programName
            ? ` • ${customer.corporateProgramStatus.programName}`
            : ""
        }`,
        type: "CP",
      });

      if ((customer.corporateProgramStatus.employeeSponsoredRemaining || 0) > 0) {
        indicators.push({
          code: "CS",
          label: `${
            customer.corporateProgramStatus.employeeSponsoredRemaining
          } sponsored corporate session${
            customer.corporateProgramStatus.employeeSponsoredRemaining === 1
              ? ""
              : "s"
          } available this month`,
          type: "CS",
        });
      }
    }

    // Check for Unified Membership / Flexible Session Credits membership
    if (
      customer.creditSystemType === "unified_shared" &&
      (customer.sharedMembershipCurrentPeriodEnd ||
        customer.sharedMembershipSessionUsage?.remaining > 0 ||
        customer.sharedMembershipSessionUsage?.booked > 0 ||
        customer.sharedMembershipSessionUsage?.totalSessions > 0)
    ) {
      const label = getUnifiedMembershipDisplayName();
      indicators.push({
        code: "FM",
        label: `${label} - Flexible Membership`,
        type: "FM",
      });
    }

    // Check for Flexible Session Package (FP)
    if (
      customer.creditSystemType === "unified_shared" &&
      customer.sharedPackageName &&
      customer.sharedPackageName !== "none" &&
      hasUnexpiredAvailableCredits(
        customer.sharedPackageSessionUsage,
        customer.sharedPackageExpiresAt
      )
    ) {
      indicators.push({
        code: "FP",
        label: `${customer.sharedPackageName} - Flexible Session Package`,
        type: "FP",
      });
    }

    // Check for Flexible Drop-in (FD)
    if (
      customer.creditSystemType === "unified_shared" &&
      hasAvailableOrBookedCredits(customer.sharedDropInCount)
    ) {
      indicators.push({
        code: "FD",
        label: "Flexible Session Drop-In",
        type: "FD",
      });
    }

    // Check for Flexible Gift (FG)
    if (
      customer.creditSystemType === "unified_shared" &&
      hasUnexpiredAvailableCredits(
        customer.sharedGiftedCredits,
        customer.sharedGiftedCredits?.expiresAt
      )
    ) {
      indicators.push({
        code: "FG",
        label: "Flexible Session Gift",
        type: "FG",
      });
    }

    // Check for Sauna Membership (SM)
    if (
      customer.saunaMembershipStatus === "active" ||
      customer.saunaMembershipStatus === "trialing" ||
      customer.saunaMembershipStatus === "Active" ||
      customer.saunaMembershipStatus === "Trialing"
    ) {
      indicators.push({
        code: "SM",
        label: "Sauna Membership",
        type: "SM",
      });
    }

    // Check for Red Light Membership (RM)
    if (
      customer.redLightMembershipStatus === "active" ||
      customer.redLightMembershipStatus === "trialing" ||
      customer.redLightMembershipStatus === "Active" ||
      customer.redLightMembershipStatus === "Trialing"
    ) {
      indicators.push({
        code: "RM",
        label: "Red Light Membership",
        type: "RM",
      });
    }

    // Check for Sauna Package (SP)
    if (
      customer.saunaPackageName &&
      customer.saunaPackageName !== "none" &&
      customer.saunaPackageSessionUsage?.remaining > 0
    ) {
      indicators.push({
        code: "SP",
        label: customer.saunaPackageName,
        type: "SP",
      });
    }

    // Check for Red Light Package (RP)
    if (
      customer.redLightPackageName &&
      customer.redLightPackageName !== "none" &&
      customer.redLightPackageSessionUsage?.remaining > 0
    ) {
      indicators.push({
        code: "RP",
        label: customer.redLightPackageName,
        type: "RP",
      });
    }

    // Check for Sauna Drop-in (SD)
    if (customer.dropInSaunaCount?.remaining > 0) {
      indicators.push({
        code: "SD",
        label: "Sauna Drop-in",
        type: "SD",
      });
    }

    // Check for Red Light Drop-in (RD)
    if (customer.dropInRedLightCount?.remaining > 0) {
      indicators.push({
        code: "RD",
        label: "Red Light Drop-in",
        type: "RD",
      });
    }

    // Check for Sauna Gift (SG)
    if (customer.giftedSaunaCredits?.remaining > 0) {
      indicators.push({
        code: "SG",
        label: "Sauna Gift",
        type: "SG",
      });
    }

    // Check for Red Light Gift (RG)
    if (customer.giftedRedLightCredits?.remaining > 0) {
      indicators.push({
        code: "RG",
        label: "Red Light Gift",
        type: "RG",
      });
    }

    // Check for Wellness Wednesday (WW)
    // Trigger: active WW credits remaining; matches dropInSaunaCount/giftedSaunaCredits
    // convention. Field surfaces on customer-list responses once the backend
    // projection edit (CUST-WW-BADGE-001 Simone PR) lands.
    if (customer.wellnessWednesdayCredits?.remaining > 0) {
      indicators.push({
        code: "WW",
        label: "Wellness Wednesday",
        type: "WW",
      });
    }

    return indicators;
  };

  // Define color mapping for different product types
  const colorMap = {
    SM: { bg: "#4CAF50", text: "white" }, // Green for Sauna Membership
    RM: { bg: "#F44336", text: "white" }, // Red for Red Light Membership
    SP: { bg: "#8BC34A", text: "black" }, // Light Green for Sauna Package
    RP: { bg: "#FF5722", text: "white" }, // Deep Orange for Red Light Package
    SD: { bg: "#CDDC39", text: "black" }, // Lime for Sauna Drop-in
    RD: { bg: "#FF9800", text: "black" }, // Orange for Red Light Drop-in
    SG: { bg: "#2196F3", text: "white" }, // Blue for Sauna Gift
    RG: { bg: "#9C27B0", text: "white" }, // Purple for Red Light Gift
    CP: { bg: "#243447", text: "white" }, // Slate for corporate program
    CS: { bg: "#2b7a78", text: "white" }, // Blue-green for sponsored sessions
    FM: { bg: "#0f5c6e", text: "white" }, // Teal for flexible membership
    FP: { bg: "#c49a3a", text: "black" }, // Gold for flexible package
    FD: { bg: "#7f9f80", text: "white" }, // Sage for flexible drop-in
    FG: { bg: "#b56f7a", text: "white" }, // Rose for flexible gift
    // WW (Wellness Wednesday): cyan-teal #00838F (Material Cyan 800).
    // Picked for visual distinctness from RM (#F44336 red), SM (#4CAF50 green),
    // SG (#2196F3 blue), FM (#0f5c6e deep teal), CS (#2b7a78 blue-green), and
    // RG (#9C27B0 purple). Cyan-teal evokes "wellness/water/spa" semantics for
    // mid-week wellness sessions while staying clearly outside the existing
    // teal/blue/green clusters. White text on #00838F passes WCAG AA (~5.0:1).
    // Cross-ref: docs/design/_workforce-design-system.md §1 — does NOT collide
    // with --status-* tokens. Zuri can ratify or refine in a follow-up design
    // pass; CUST-WW-BADGE-001 §Coordination Notes flagged this as Zuri's call.
    WW: { bg: "#00838F", text: "white" }, // Cyan-teal for Wellness Wednesday
  };

  const indicators = getProductIndicators();

  if (indicators.length === 0) {
    return <span className="no-products">None</span>;
  }

  return (
    <div className="product-indicators">
      {indicators.map((indicator, index) => (
        <span
          key={index}
          className="product-badge"
          title={indicator.label}
          aria-label={indicator.label}
          style={{
            backgroundColor: colorMap[indicator.type]?.bg || "#6c757d",
            color: colorMap[indicator.type]?.text || "white",
          }}
        >
          {indicator.code}
        </span>
      ))}
    </div>
  );
};

/**
 * CustomerList component for staff to view and manage customers
 * Provides filtering, sorting, and pagination capabilities
 */
const CustomerList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get("status") || ""
  );
  const [sortField, setSortField] = useState(
    searchParams.get("sortField") || "createdAt"
  );
  const [sortDirection, setSortDirection] = useState(
    searchParams.get("sortDirection") || "desc"
  );
  const [currentPage, setCurrentPage] = useState(
    Number(searchParams.get("page")) || 1
  );
  const [totalPages, setTotalPages] = useState(1);
  const [analytics, setAnalytics] = useState(null);
  // Fetch customers with current filters, sorting, and pagination
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);

        // Build query parameters
        const params = {
          page: currentPage,
          limit: 10,
          sortField,
          sortDirection,
        };

        if (searchTerm) {
          params.search = searchTerm;
        }

        if (statusFilter) {
          params.status = statusFilter;
        }

        // Fetch customers
        const response = await staffAPI.getCustomers(params);
        setCustomers(response.data.customers);
        setTotalPages(response.data.pagination.totalPages);
      } catch (err) {
        handleApiError(
          err,
          "CustomerList",
          setError,
          "Error fetching customers. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [currentPage, searchTerm, statusFilter, sortField, sortDirection]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (currentPage > 1) {
      nextParams.set("page", String(currentPage));
    }

    if (searchTerm) {
      nextParams.set("search", searchTerm);
    }

    if (statusFilter) {
      nextParams.set("status", statusFilter);
    }

    if (sortField !== "createdAt") {
      nextParams.set("sortField", sortField);
    }

    if (sortDirection !== "desc") {
      nextParams.set("sortDirection", sortDirection);
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    currentPage,
    searchParams,
    searchTerm,
    setSearchParams,
    sortDirection,
    sortField,
    statusFilter,
  ]);

  // Fetch customer analytics
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const response = await staffAPI.getCustomerAnalytics();
        setAnalytics(response.data);
      } catch (err) {
        handleApiError(
          err,
          "CustomerList.analytics",
          (message) => console.error(message),
          "Error fetching customer analytics"
        );
      }
    };

    fetchAnalytics();
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page on new filter
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (field === sortField) {
      // If clicking the same field, toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // If clicking a new field, set it as the sort field with asc direction
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to first page on new sort
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format renewal information for display
  const formatRenewalInfo = (customer) => {
    if (!customer.renewalInfo) return "N/A";

    const { renewalInfo } = customer;

    if (!renewalInfo.hasActiveSubscription) {
      return "No Active Subscription";
    }

    if (!renewalInfo.nextRenewalDate) {
      return "N/A";
    }

    const renewalDate = new Date(renewalInfo.nextRenewalDate);
    return renewalDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC", // Render the date in UTC
    });
  };

  // Navigate to customer profile
  const viewCustomerProfile = (customerId) => {
    const queryString = searchParams.toString();
    navigate(`/staff/customers/${customerId}`, {
      state: {
        returnTo: `/staff/customers${queryString ? `?${queryString}` : ""}`,
      },
    });
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Render loading state
  if (loading && (!customers || customers.length === 0)) {
    return <div className="loading">Loading customers...</div>;
  }

  // Render error state
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // Ensure customers is always an array
  const safeCustomers = customers || [];

  return (
    <StaffLayout>
      <div className="customer-list-container">
        {/* Header with Analytics Cards */}
        <div className="page-header">
          <h1>Customer Management</h1>
        </div>

        {/* Analytics Cards */}
        <div className="analytics-cards">
          {analytics && (
            <>
              <div className="analytics-card">
                <div className="analytics-card-value">
                  {analytics.totalCustomers}
                </div>
                <div className="analytics-card-label">Total Customers</div>
              </div>
              <div className="analytics-card">
                <div className="analytics-card-value">
                  {analytics.newCustomers}
                </div>
                <div className="analytics-card-label">New (30 days)</div>
              </div>
              <div className="analytics-card">
                <div className="analytics-card-value">
                  ${analytics.avgLtv?.toFixed(2) || "0.00"}
                </div>
                <div className="analytics-card-label">Avg. Lifetime Value</div>
              </div>
              <div className="analytics-card">
                <div className="analytics-card-value">
                  {analytics.activeMembers || 0}
                </div>
                <div className="analytics-card-label">Active Members</div>
              </div>
            </>
          )}
        </div>

        {/* Search and Filters */}
        <div className="search-filter-container">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <div className="filter-box">
            <FaFilter className="filter-icon" />
            <select
              value={statusFilter}
              onChange={handleStatusFilterChange}
              className="status-filter"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="at_risk">At Risk</option>
              <option value="vip">VIP</option>
            </select>
          </div>
        </div>

        {/* Content Card */}
        <div className="content-card">
          <div className="card-header">
            <h2>Customer List</h2>
            <div className="card-actions">
              <button
                className="refresh-button"
                onClick={() => {
                  setCurrentPage(1);
                  setSearchTerm("");
                  setStatusFilter("");
                }}
              >
                Reset Filters
              </button>
            </div>
          </div>

          {loading && customers.length === 0 ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading customers...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    {/* ESIGN-UX-REDESIGN-001 (Story 2 Surface 2) — mini-icon
                        leftmost column. Header is decorative; the icons
                        below carry their own aria-labels. */}
                    <th className="esign-status-col" aria-label="ESIGN status">
                      <span className="visually-hidden">ESIGN</span>
                    </th>
                    <th onClick={() => handleSortChange("lastName")}>
                      Name {getSortIndicator("lastName")}
                    </th>
                    <th onClick={() => handleSortChange("email")}>
                      Email {getSortIndicator("email")}
                    </th>
                    <th onClick={() => handleSortChange("contactNumber")}>
                      Phone {getSortIndicator("contactNumber")}
                    </th>
                    <th onClick={() => handleSortChange("customerStatus")}>
                      Status {getSortIndicator("customerStatus")}
                    </th>
                    <th onClick={() => handleSortChange("createdAt")}>
                      Customer Since {getSortIndicator("createdAt")}
                    </th>
                    <th onClick={() => handleSortChange("products")}>
                      Products {getSortIndicator("products")}
                    </th>
                    <th>Next Renewal</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {safeCustomers.length > 0 ? (
                    safeCustomers.map((customer) => (
                      <tr key={customer._id}>
                        <td className="esign-status-col">
                          <EsignStatusIcon
                            overallStatus={customer.esignOverallStatus}
                          />
                        </td>
                        <td>
                          <CustomerNameLink customerId={customer._id}>
                            {customer.firstName} {customer.lastName}
                            {customer.preferredName &&
                              ` (${customer.preferredName})`}
                          </CustomerNameLink>
                        </td>
                        <td>{customer.email}</td>
                        <td>{customer.contactNumber || "N/A"}</td>
                        <td>
                          <span
                            className={`status-badge status-${
                              customer.computedStatus || "new"
                            }`}
                          >
                            {customerStatusLabel(customer.computedStatus)}
                          </span>
                          {isVipCustomer(customer) && (
                            <span className="status-badge status-vip">VIP</span>
                          )}
                        </td>
                        <td>{formatDate(customer.createdAt)}</td>
                        <td>
                          <ProductIndicators customer={customer} />
                        </td>
                        <td>{formatRenewalInfo(customer)}</td>
                        <td className="actions-cell">
                          <button
                            className="action-btn action-btn-primary"
                            onClick={() => viewCustomerProfile(customer._id)}
                          >
                            <FaEye className="button-icon" />
                            <span>View</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="empty-state">
                        No customers found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-button"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-button"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </StaffLayout>
  );
};

export default CustomerList;
